import React, { useEffect, useRef } from 'react'
import type { BaseProvider } from '../providers/base'
import { AnalyticsContext } from './analyticsContext'
import { analyticsManager } from '../analytics-manager'

interface Props {
  providers: BaseProvider[]
  children: React.ReactNode
}

export const AnalyticsProvider: React.FC<Props> = ({ providers, children }) => {
  const providersRef = useRef<BaseProvider[]>([])

  useEffect(() => {
    // Cleanup existing providers
    providersRef.current.forEach((provider) => {
      analyticsManager.removeProvider(provider)
    })
    providersRef.current = []

    // Add new providers
    for (const provider of providers) {
      analyticsManager.addProvider(provider)
      providersRef.current.push(provider)
    }

    // Cleanup on unmount or providers change
    return () => {
      providersRef.current.forEach((provider) => {
        analyticsManager.removeProvider(provider)
      })
      providersRef.current = []
    }
  }, [providers])

  const value = React.useMemo(
    () => ({
      trackEvent: analyticsManager.trackEvent,
      setGlobalContext: analyticsManager.setGlobalContext,
      clearGlobalContext: analyticsManager.clearGlobalContext,
      setVehicle: analyticsManager.setVehicle,
      clearVehicle: analyticsManager.clearVehicle,
      setUser: analyticsManager.setUser,
      clearUser: analyticsManager.clearUser,
    }),
    []
  )

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  )
}
